import PropTypes from 'prop-types'
import { memo, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { usePersonaContext } from 'app/services/PersonaProvider/PersonaProvider'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
import useDescribeAccessoryCompatibility from 'app/services/hooks/useDescribeAccessoryCompatibility'
import prettyPrice from 'app/util/prettyPrice'
import Button from 'components/core/Button/Button'
import LineItem from 'components/core/LineItem/LineItem'
import AdaptiveImage from 'components/elements/AdaptiveImage/AdaptiveImage'
import Tooltip from 'components/interface/Tooltip/Tooltip'
import noop from 'lib/util/noop'

/**
 * The `Item`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function Item (props) {
  const {
    brand,
    className,
    color,
    gender,
    id,
    name,
    onRemove,
    price,
    primaryUtility,
    productImage,
    style,
    thumbnail,
    type
  } = props
  const { maticToUsdMultiplier } = usePersonaContext()
  const { preferredCurrency } = useUserContext()
  const {
    labelColor,
    labelDescriptiveCompatibilityList,
    labelFaction,
    personaList
  } = useDescribeAccessoryCompatibility(type, gender, style, color)
  const priceRes = useMemo(
    () => prettyPrice(price, preferredCurrency, maticToUsdMultiplier),
    [price, maticToUsdMultiplier, preferredCurrency]
  )

  /**
   * Handle click remove
   */
  const handleClickRemove = useCallback(() => {
    onRemove(id)
  }, [id, onRemove])

  return (
    <LineItem className={className}>
      <LineItem.Media Tag={Link} to={`/marketplace/product/${id}`}>
        <AdaptiveImage
          src={productImage}
          fallbackSrc={thumbnail}
          sizes={[100, 250, 300, 500]}
        />
      </LineItem.Media>
      <LineItem.Content>
        <LineItem.Header>
          <LineItem.Block>
            <LineItem.Author
              Tag={Link}
              to={`/marketplace/collections/${brand?.id}`}
              thumbnail={brand?.image}
            >
              {brand?.title}
            </LineItem.Author>
            <LineItem.Title>{name}</LineItem.Title>
          </LineItem.Block>
          <LineItem.Value>{priceRes}</LineItem.Value>
        </LineItem.Header>

        <LineItem.Footer>
          <LineItem.Tags>
            <LineItem.Tag
              Tag="a"
              useTooltip={true}
              tooltipContent={
                <Tooltip delayShow={0}>
                  <Tooltip.Header>Accessory ID</Tooltip.Header>
                  <Tooltip.Body>#{id}</Tooltip.Body>
                </Tooltip>
              }
            >
              #{id}
            </LineItem.Tag>
            <LineItem.Tag
              Tag="a"
              useTooltip={true}
              tooltipContent={
                <Tooltip delayShow={0}>
                  <Tooltip.Header>Compatibility</Tooltip.Header>
                  <Tooltip.Body>
                    <span>
                      This accessory is {labelDescriptiveCompatibilityList}{' '}
                      Avatars.
                    </span>
                  </Tooltip.Body>
                </Tooltip>
              }
            >
              {labelFaction}
            </LineItem.Tag>
            {personaList.map((label, i) => (
              <LineItem.Tag
                key={i}
                Tag="a"
                useTooltip={true}
                tooltipContent={
                  <Tooltip delayShow={0}>
                    <Tooltip.Header>Compatibility</Tooltip.Header>
                    <Tooltip.Body>
                      <span>
                        This accessory is {labelDescriptiveCompatibilityList}{' '}
                        Avatars.
                      </span>
                    </Tooltip.Body>
                  </Tooltip>
                }
              >
                {label}
              </LineItem.Tag>
            ))}

            {labelColor && (
              <LineItem.Tag
                Tag="a"
                useTooltip={true}
                tooltipContent={
                  <Tooltip delayShow={0}>
                    <Tooltip.Header>Compatibility</Tooltip.Header>
                    <Tooltip.Body>
                      <span>
                        This accessory is {labelDescriptiveCompatibilityList}{' '}
                        Avatars.
                      </span>
                    </Tooltip.Body>
                  </Tooltip>
                }
              >
                {labelColor}
              </LineItem.Tag>
            )}
            <LineItem.Tag
              Tag="a"
              useTooltip={true}
              tooltipContent={
                <Tooltip delayShow={0}>
                  <Tooltip.Header>Primary Utility</Tooltip.Header>
                  <Tooltip.Body>{primaryUtility?.longLabel}</Tooltip.Body>
                </Tooltip>
              }
            >
              {primaryUtility?.label}
            </LineItem.Tag>
          </LineItem.Tags>
          <LineItem.Options>
            <Button
              size="sm"
              color="j"
              borderRadius="c"
              icon="close"
              onClick={handleClickRemove}
            >
              Remove
            </Button>
          </LineItem.Options>
        </LineItem.Footer>
      </LineItem.Content>
    </LineItem>
  )
}

/** @type {object} */
Item.propTypes = {
  brand: PropTypes.shape(),
  className: PropTypes.string,
  color: PropTypes.string,
  gender: PropTypes.arrayOf(PropTypes.string),
  style: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.number,
  layer: PropTypes.string,
  layerLabel: PropTypes.string,
  name: PropTypes.string,
  onRemove: PropTypes.func,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  primaryUtility: PropTypes.shape({
    label: PropTypes.string,
    longLabel: PropTypes.string
  }),
  productImage: PropTypes.string,
  thumbnail: PropTypes.string,
  type: PropTypes.string
}

/** @type {object} */
Item.defaultProps = {
  brand: {},
  className: '',
  color: '',
  gender: [],
  style: [],
  id: 0,
  layer: '',
  layerLabel: '',
  name: '',
  onRemove: noop,
  price: 0,
  productImage: '',
  thumbnail: '',
  primaryUtility: {
    label: 'Collectible',
    longLabel: 'Collectible'
  },
  type: ''
}

// Memoize
export default memo(Item)
